// ══════════════════════════════════════════════════════════════════
//  Query keys centralizadas do TanStack Query.
//  Uma fonte única evita strings soltas e facilita invalidação.
//  Convenção: qk.<domínio>.<recurso>(...args) -> array de chave.
// ══════════════════════════════════════════════════════════════════

export const qk = {
  config: {
    commissionRates: (workspaceId: string) =>
      ['config', 'commissionRates', workspaceId] as const,
    workspaceOrigins: (workspaceId: string) =>
      ['config', 'workspaceOrigins', workspaceId] as const,
    workspaceOriginCounts: (workspaceId: string) =>
      ['config', 'workspaceOriginCounts', workspaceId] as const,
  },
  users: {
    byRole: (workspaceId: string, role: 'sdr' | 'closer') =>
      ['users', 'byRole', workspaceId, role] as const,
  },
  filterOptions: (workspaceId: string) =>
    ['filterOptions', workspaceId] as const,
  // Tags do workspace — cache único do TagPicker (Kanban, Atendimento, …),
  // para que uma tag criada em qualquer tela já apareça nas outras.
  tags: (workspaceId: string) => ['tags', workspaceId] as const,
  activities: (workspaceId: string, assigneeId: string | null, type: string | null, funnelId: string | null) =>
    ['activities', workspaceId, assigneeId ?? 'all', type ?? 'all', funnelId ?? 'all'] as const,
  leads: (workspaceId: string) => ['leads', workspaceId] as const,
  // Variante do fetch de leads com recorte de visibilidade (Kanban Enterprise).
  // Mantém o prefixo ['leads', ws] para ser invalidada junto com o fetch base.
  leadsScoped: (workspaceId: string, scopeKey: string) =>
    ['leads', workspaceId, scopeKey] as const,
  // IDs dos membros de um time (regra "leads do meu time" no Enterprise).
  teamMemberIds: (timeId: string) => ['teamMemberIds', timeId] as const,
  // Abas do LeadModal (lead-scoped) — prefetchadas ao abrir o modal.
  leadActivities: (leadId: string) => ['leadActivities', leadId] as const,
  leadHistory: (leadId: string) => ['leadHistory', leadId] as const,
  leadMeetings: (leadId: string) => ['leadMeetings', leadId] as const,
  // Entradas de marketing do lead (RPC get_lead_mkt_entries) — só a tela
  // de detalhe consome; dado imutável na prática, staleTime alto.
  leadMktEntries: (leadId: string) => ['leadMktEntries', leadId] as const,
  // Comissões por período (+ filtro closer/sdr). Prefix ['commissions', ws]
  // permite invalidar tudo do workspace de uma vez.
  commissions: (workspaceId: string, period: string, closerId: string | null, sdrId: string | null) =>
    ['commissions', workspaceId, period, closerId ?? null, sdrId ?? null] as const,
  // Reuniões da semana (Agenda). Prefix ['meetings', ws] p/ invalidar tudo.
  meetings: (workspaceId: string, startISO: string, endISO: string, closerId: string | null, sdrId: string | null) =>
    ['meetings', workspaceId, startISO, endISO, closerId ?? null, sdrId ?? null] as const,
  // Realização de metas (GoalsPage). scope=aba; mode+anchor=período.
  metas: (workspaceId: string, scope: string, mode: string, anchor: string, funnelId: string | null) =>
    ['metas', workspaceId, scope, mode, anchor, funnelId ?? null] as const,
  // Minhas metas (6 meses do usuário logado).
  minhasMetas: (workspaceId: string, userId: string, period: string) =>
    ['minhasMetas', workspaceId, userId, period] as const,
  // SDR/Closer do time (dropdown da Central de Atividades — só gestor).
  activityTeam: (workspaceId: string) => ['activityTeam', workspaceId] as const,
  // Caixa de entrada do sino. Prefix ['notifications', userId] permite
  // invalidar lista + contador juntos quando o realtime acusa mudança.
  notifications: (userId: string) => ['notifications', userId, 'list'] as const,
  notificationsUnread: (userId: string) => ['notifications', userId, 'unread'] as const,
  notificationPrefs: (userId: string, workspaceId: string) =>
    ['notificationPrefs', userId, workspaceId] as const,
  // Convites + cadastros pendentes (Settings → Controle de acesso).
  accessControl: (workspaceId: string) => ['accessControl', workspaceId] as const,
  // Editor de metas (Settings). periodsKey = periods.join(',').
  metasSettings: (workspaceId: string, scope: string, periodsKey: string, funnelId: string | null) =>
    ['metasSettings', workspaceId, scope, periodsKey, funnelId ?? null] as const,
  metaUsers: (workspaceId: string) => ['metaUsers', workspaceId] as const,
  // Painel pessoal (fetch agregado). role/ownerFlag mudam o recorte.
  meuPainel: (workspaceId: string, userId: string, period: string, role: string, ownerFlag: boolean) =>
    ['meuPainel', workspaceId, userId, period, role, ownerFlag] as const,
  pipelineIds: (workspaceId: string, funnelId: string | null, role: string | null) =>
    ['pipelineIds', workspaceId, funnelId ?? null, role ?? null] as const,
  funnels: (workspaceId: string) => ['funnels', workspaceId] as const,
  // Empresas (página Empresas — Enterprise). Prefix ['empresas', ws] p/ invalidar tudo.
  empresas: (workspaceId: string) => ['empresas', workspaceId] as const,
  // Fila de implantação (/clientes). Sem workspace na chave de propósito: a
  // área é transversal — a RPC devolve as empresas liberadas de todos eles,
  // e trocar de workspace não muda o resultado.
  clientesImplantacao: () => ['clientesImplantacao'] as const,
  clienteImplantacao: (empresaId: string) =>
    ['clientesImplantacao', empresaId] as const,
  // Clientes do Hub (/clientes, aba Hub) — espelho de TODAS as empresas do
  // BlueHub, de todos os workspaces. Mesma lógica do prefixo sem workspace:
  // a RPC decide quem vê, trocar de workspace não muda o resultado.
  clientesHub: () => ['clientesHub'] as const,
  ultimaSincronizacaoHub: () => ['clientesHub', 'ultimaSincronizacao'] as const,
  // Contas do Hub vinculadas a UMA empresa do CRM (ficha, cabeçalho) — uma
  // linha por CNPJ do grupo (matriz + filiais). Prefixo próprio porque a
  // chave é por empresaId, não por todo o espelho.
  contasHubDaEmpresa: (empresaId: string) => ['contasHubDaEmpresa', empresaId] as const,
  // Trava do cadastro pelo Hub (cs.empresa_travada_pelo_hub) — por empresa.
  empresaTravadaPeloHub: (empresaId: string) => ['empresaTravadaPeloHub', empresaId] as const,
  // Ficha do cliente (/clientes/:empresaId) — uma rota, dois públicos (CS ×
  // Implantação); o nível vem da RPC, não da chave.
  fichaCliente: (empresaId: string) => ['fichaCliente', empresaId] as const,
  gatilhosEmpresa: (empresaId: string) => ['gatilhosEmpresa', empresaId] as const,
  // Clientes Ativos (Enterprise): empresas com contrato enviado. A RLS
  // decide o recorte, então a chave não leva workspace.
  carteiraContratos: () => ['carteiraContratos'] as const,
  financeGlobal: (ano: number, mes: number | null) => ['financeGlobal', ano, mes ?? null] as const,
  dashboard: (kind: string, p: {
    workspaceId?: string; funnelId?: string | null;
    startISO?: string | null; endISO?: string | null; userCtx?: any;
    memberIds?: string[] | null;
    // Leitura do grupo enterprise (matriz + enterprise + aex). Entra na chave
    // porque troca a fonte da consulta, não só o filtro.
    crossWorkspace?: boolean;
  }) => [
    'dashboard', kind, p.workspaceId ?? '', p.funnelId ?? null,
    p.startISO ?? null, p.endISO ?? null,
    p.userCtx?.id ?? null, p.userCtx?.role ?? null, !!p.userCtx?.canViewAll,
    p.memberIds ? p.memberIds.join(',') : null,
    !!p.crossWorkspace,
  ] as const,
  // Uso da equipe (presença + produção). O recorte gestor/próprio é do
  // banco, mas entra na chave porque troca de papel muda o resultado.
  teamUsage: (workspaceId: string, days: number, canViewAll: boolean) =>
    ['teamUsage', 'report-v2', workspaceId, days, canViewAll] as const,
  // Próximas chaves serão adicionadas conforme migramos os domínios
  // (leads, pipelines, stages, activities, agenda, comissões, ...).
} as const;
